import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FiExternalLink, FiGithub } from 'react-icons/fi';
import './Projects.css';

const Projects = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const projects = [
    {
      id: 1,
      title: 'Arth संचय',
      description: 'AI-powered goal-based wealth planning platform that uses machine learning, portfolio optimization, and Monte Carlo simulation to generate personalized investment strategies.',
      image: 'https://res.cloudinary.com/djnqexy8g/image/upload/v1771057638/ArthSanchay_gqad1y.png',
      technologies: ['React', 'Node.js', 'MongoDB', 'Stripe', 'JWT'],
      liveUrl: 'https://example.com',
      githubUrl: 'https://github.com/Gayatrii4506/ArthSanchay',
      featured: true,
      status: 'in-progress',
    },
    {
      id: 2,
      title: 'RAKSHA',
      description: 'RAKSHA is a secure web-based complaint management system that allows users to submit, track, and manage complaints with role-based access and real-time notifications.',
      image: 'https://res.cloudinary.com/djnqexy8g/image/upload/v1771057904/RAKSHA_ryalhp.png',
      technologies: ['React', 'TypeScript', 'Socket.io', 'Express', 'MongoDB'],
      liveUrl: 'https://example.com',
      githubUrl: 'https://github.com/Gayatrii4506/RAKSHA',
      featured: true,
      status: 'in-progress',
    },
    {
      id: 3,
      title: 'Weather Dashboard',
      description: 'A responsive weather application with location-based forecasts, interactive maps, and detailed weather analytics.',
      image: 'https://images.unsplash.com/photo-1504608524841-42fe6f032b4b?w=600&h=400&fit=crop',
      technologies: ['React', 'OpenWeather API', 'Chart.js', 'Tailwind CSS'],
      liveUrl: 'https://example.com',
      githubUrl: 'https://github.com/Gayatrii4506/weather-app',
      featured: false,
      status: 'in-progress',
    },
    {
      id: 4,
      title: 'Student Management System',
      description: 'A comprehensive student portal with features for attendance tracking, grade management, course enrollment, and academic progress monitoring.',
      image: 'https://images.unsplash.com/photo-1523240795612-9a054b0db644?w=600&h=400&fit=crop',
      technologies: ['HTML', 'CSS', 'JavaScript', 'PHP', 'MySQL'],
      liveUrl: 'https://example.com',
      githubUrl: 'https://github.com/Gayatrii4506/student-portal',
      featured: false,
      status: 'in-progress',
    },
    {
      id: 5,
      title: 'Online Resume Builder',
      description: 'A Java-based application that allows users to create, customize, and download professional resumes quickly through a simple and user-friendly interface',
      image: 'https://res.cloudinary.com/djnqexy8g/image/upload/v1771062097/Screenshot_2026-02-14_151101_vqoiix.png',
      technologies: ['JavaFX', 'Swing', 'Spring Boot'],
      liveUrl: 'https://example.com',
      githubUrl: 'https://github.com/Gayatrii4506/recipe-finder',
      featured: false,
      status: 'in-progress',
    },
    {
      id: 6,
      title: 'Shodh',
      description: 'Shodh is a modern full-stack web platform that empowers researchers to discover groundbreaking project ideas, form collaborative teams, and advance scientific innovation together',
      image: 'https://res.cloudinary.com/djnqexy8g/image/upload/v1771059904/Screenshot_2026-02-14_143349_usaaqh.png',
      technologies: ['React', 'Framer Motion', 'CSS3', 'Netlify'],
      liveUrl: 'https://example.com',
      githubUrl: 'https://github.com/Gayatrii4506/Shodh',
      featured: false,
      status: 'in-progress',
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut',
      },
    },
  };

  return (
    <section id="projects" className="projects section">
      <div className="container">
        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          <motion.h2 className="section-title" variants={itemVariants}>
            Featured Projects
          </motion.h2>

          <div className="projects-grid">
            {projects.map((project, index) => (
              <motion.div
                key={project.id}
                className={`project-card ${project.featured ? 'featured' : ''}`}
                variants={itemVariants}
                whileHover={{ y: -10 }}
                transition={{ duration: 0.3 }}
              >
                <div className="project-image">
                  {project.status === 'in-progress' && (
                    <div className="project-badge in-progress">
                      In Progress
                    </div>
                  )}
                  <img src={project.image} alt={project.title} />
                  <div className="project-overlay">
                    <div className="project-overlay-buttons">
                      {project.status === 'in-progress' || !project.liveUrl ? (
                        <button className="btn-overlay btn-overlay-disabled" disabled>
                          Coming Soon
                        </button>
                      ) : (
                        <a
                          href={project.liveUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn-overlay btn-overlay-live"
                          aria-label="View live project"
                        >
                          View Live Demo
                        </a>
                      )}

                      <a
                        href={project.githubUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-overlay btn-overlay-github"
                        aria-label="View source code"
                      >
                        View on GitHub
                      </a>
                    </div>
                  </div>
                </div>

                <div className="project-content">
                  <h3 className="project-title">{project.title}</h3>
                  <p className="project-description">{project.description}</p>

                  <div className="project-technologies">
                    {project.technologies.map((tech, techIndex) => (
                      <span key={techIndex} className="tech-tag">
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div className="projects-cta" variants={itemVariants}>
            <p>Want to see more of my work?</p>
            <motion.a
              href="https://github.com/Gayatrii4506"
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-primary"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <FiGithub />
              View All Projects
            </motion.a>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Projects;